/* global BigInt */

import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

const TOTAL_SLOTS = 96;
const SLOTS_PER_ROW = 48;

function rangesToBitmask(ranges){
  let mask = 0n;
  for (const [s,e] of ranges){
    for(let i=s;i<=e;i++){
      mask |= (1n << BigInt(i));
    }
  }
  return mask;
}

function bitmaskToRanges(mask){
  const ranges=[];
  let start=null;

  for(let i=0;i<TOTAL_SLOTS;i++){
    const active = (mask & (1n << BigInt(i))) !== 0n;
    if(active && start===null){
      start=i;
    }
    if(!active && start!==null){
      ranges.push([start,i-1]);
      start=null;
    }
  }

  if(start!==null){
    ranges.push([start,TOTAL_SLOTS-1]);
  }

  return ranges;
}

function bitmaskToHex(mask){
  return mask.toString(16);
}

function hexToBitmask(hex){
  const cleanHex = hex.replaceAll('"', '');
  return BigInt(`0x${cleanHex}`);
}

function slotToTime(slot) {
  const h = Math.floor(slot / 4);
  const m = (slot % 4) * 15;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
}

function slotInRanges(slot, ranges) {
  return ranges.some(([s,e]) => slot>=s && slot<=e);
}

function mergeRanges(ranges) {
  if (!ranges.length) return [];

  const sorted = [...ranges].sort((a,b)=>a[0]-b[0]);
  const result = [sorted[0]];

  for (let i=1;i<sorted.length;i++) {
    const [s,e] = sorted[i];
    const last = result[result.length-1];

    if (s <= last[1] + 1) {
      last[1] = Math.max(last[1], e);
    } else {
      result.push([s,e]);
    }
  }

  return result;
}

export default function TimelineRange({ value=[], onChange=() => {} }) {

  const initialRanges = typeof value === "string"
    ? bitmaskToRanges(hexToBitmask(value))
    : value;

  const [ranges, setRanges] = useState(initialRanges);
  const [dragStart, setDragStart] = useState(null);
  const [preview, setPreview] = useState(null);

  const removeRangeAtSlot = (slot)=>{
    const filtered = ranges.filter(
      ([s,e]) => !(slot>=s && slot<=e)
    );
    setRanges(filtered);
    const mask = rangesToBitmask(filtered);
    onChange(bitmaskToHex(mask));
  };

   const handleMouseDown = (slot, event)=>{
    
       if (event.shiftKey) {
         removeRangeAtSlot(slot);
         return;
       }
    
       setDragStart(slot);
       setPreview([slot,slot]);
     };

  const handleMouseEnter = (slot)=>{
    if(dragStart===null) return;

    const start = Math.min(dragStart,slot);
    const end = Math.max(dragStart,slot);

    setPreview([start,end]);
  };

  const handleMouseUp = ()=> {
    if (preview) {
      const merged = mergeRanges([...ranges, preview]);
      setRanges(merged);
      const mask = rangesToBitmask(merged);
      const hexMask = bitmaskToHex(mask);
      onChange(hexMask);
    }

    setDragStart(null);
    setPreview(null);
  };

  return (
    <Box
      sx={{width:900}}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >

      <Typography sx={{mb:2}}>
        Selected ranges: {ranges.map(r=>`${slotToTime(r[0])}-${slotToTime(r[1]+1)}`).join(", ")}
      </Typography>

      {[0,1].map(row=>{

        const startSlot = row * SLOTS_PER_ROW;

        return (
          <Box key={row} sx={{mb:3}}>

            {/* hour labels */}
            <Box
              sx={{
                display:"grid",
                gridTemplateColumns:"repeat(12,1fr)",
                fontSize:12,
                color:"#616161",
                mb:0.5,
                fontWeight:500
              }}
            >
              {Array.from({length:12}).map((_,i)=>(
                <Box key={i} sx={{textAlign:"center"}}>
                  {String(i + row*12).padStart(2,"0")}
                </Box>
              ))}
            </Box>

            {/* timeline */}
            <Box
              sx={{
                display:"grid",
                gridTemplateColumns:"repeat(48,1fr)",
                gap:"2px",
                userSelect:"none"
              }}
            >
              {Array.from({length:SLOTS_PER_ROW}).map((_,i)=>{

                const slot = startSlot + i;

                const active =
                  slotInRanges(slot,ranges) ||
                  (preview && slot>=preview[0] && slot<=preview[1]);

                const hourSeparator = i%4===0;

                return (
                  <Box
                    key={slot}
                    onMouseDown={(e)=>handleMouseDown(slot, e)}
                    onMouseEnter={()=>handleMouseEnter(slot)}
                    title={slotToTime(slot)}
                    sx={{
                      height:28,
                      backgroundColor: active ? "#1976d2" : "#eeeeee",
                      borderLeft: hourSeparator
                        ? "3px solid #424242"
                        : "1px solid #d6d6d6",
                      borderRadius:"2px",
                      cursor:"pointer",
                      transition:"background 0.15s",
                      "&:hover":{
                        backgroundColor: active ? "#1565c0" : "#dcdcdc"
                      }
                    }}
                  />
                );
              })}
            </Box>

          </Box>
        );
      })}
    </Box>
  );
}